import { useEffect, useState } from "react";
import { Trophy, CalendarDays, Target } from "lucide-react";
import { http } from "@/lib/api";

const METRICS = [["esito_pct", "1X2"], ["goal_pct", "GG/NG"], ["over_pct", "O/U 2.5"], ["over_1_5_pct", "O/U 1.5"], ["multigol_pct", "Multigol"], ["combo_pct", "Combo"]];
const color = (v) => (v == null ? "#334155" : v >= 60 ? "var(--win)" : v >= 45 ? "var(--draw)" : "var(--loss)");

function Bar({ value }) {
  return (
    <div className="flex items-center gap-2 min-w-[90px]">
      <div className="h-2 flex-1 rounded-full bg-slate-800 overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${value ?? 0}%`, background: color(value) }} />
      </div>
      <span className="font-mono text-xs w-9 text-right">{value == null ? "—" : `${value}%`}</span>
    </div>
  );
}

function StatsTable({ rows, testid, icon: Icon, title, showEmblem }) {
  return (
    <section className="space-y-4">
      <h3 className="font-display text-3xl font-bold uppercase flex items-center gap-2"><Icon size={22} className="text-[var(--gold)]" /> {title}</h3>
      <div className="panel overflow-x-auto">
        <table data-testid={testid} className="w-full text-sm">
          <thead>
            <tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-slate-800">
              <th className="py-3 px-4 text-left">{showEmblem ? "Competizione" : "Mese"}</th>
              <th className="py-3 px-3 text-center">Giocate</th>
              {METRICS.map(([, l]) => <th key={l} className="py-3 px-3 text-left">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="py-6 px-4 text-slate-500">Ancora nessuna partita con pronostico conclusa.</td></tr>}
            {rows.map((r) => (
              <tr key={r.key} data-testid={`${testid}-row-${r.key}`} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors">
                <td className="py-3 px-4"><div className="flex items-center gap-2 whitespace-nowrap">
                  {showEmblem && r.emblem && <span className="w-6 h-6 rounded bg-white/90 grid place-items-center p-0.5"><img src={r.emblem} alt="" className="max-w-full max-h-full object-contain" /></span>}
                  <span className="font-medium">{r.label}</span>
                </div></td>
                <td className="py-3 px-3 text-center font-mono">{r.total}</td>
                {METRICS.map(([k]) => <td key={k} className="py-3 px-3"><Bar value={r[k]} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function PublicStats() {
  const [s, setS] = useState(null);
  useEffect(() => { http.get("/public/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="panel h-64 scan" />;
  const o = s.overall;

  return (
    <div className="space-y-12">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="panel p-5 fade-up border-[var(--gold)]/40">
          <div className="text-[11px] uppercase tracking-wider text-slate-400">Partite valutate</div>
          <div data-testid="stats-total" className="font-display text-5xl font-extrabold mt-2">{o.total}</div>
        </div>
        {s.markets.map(({ key, label }, i) => (
          <div key={key} className="panel p-5 fade-up" style={{ animationDelay: `${Math.min(i + 1, 8) * 60}ms` }}>
            <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
            <div data-testid={`stats-overall-${key}_pct`} className="font-display text-4xl font-extrabold mt-2" style={{ color: color(o[`${key}_pct`]) }}>{o[`${key}_pct`] == null ? "—" : `${o[`${key}_pct`]}%`}</div>
            <div className="text-xs text-slate-500 mt-1 font-mono">{o[key] ?? 0}/{o[`${key}_n`] ?? 0} azzeccati</div>
          </div>
        ))}
      </div>
      <StatsTable rows={s.competitions} testid="stats-competitions" icon={Trophy} title="Per competizione" showEmblem />
      <StatsTable rows={s.months} testid="stats-months" icon={CalendarDays} title="Per mese" />
      <p className="text-xs text-slate-500 flex items-center gap-2"><Target size={14} /> Le percentuali si aggiornano automaticamente a fine partita.</p>
    </div>
  );
}
